import React, { useEffect, useMemo, useState, Fragment } from "react";
import axios from "../../utils/axiosInstance";
import { format } from "date-fns";
import { io } from "socket.io-client";
import BottomNav from "../../components/BottomNav";
import Sidebar from "../../components/Sidebar";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Menu, Transition } from "@headlessui/react";
import { MoreVertical, FileDown, Printer, Search, Filter } from "lucide-react";
import logo from "../../assets/images/ngc-logo.jpeg";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 8;
const FEE_PERCENT = 0.002;

export default function TransactionHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [activeTx, setActiveTx] = useState(null);

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const navigate = useNavigate();

  const getFee = (tx) =>
    tx.fee !== undefined && tx.fee !== null
      ? Number(tx.fee)
      : Number((Number(tx.amount || 0) * FEE_PERCENT).toFixed(2));

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`/api/transactions/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTransactions(res.data?.transactions || []);
      } catch (err) {
        console.error("❌ Error fetching transactions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();

    const socket = io(process.env.REACT_APP_API_URL, {
      auth: { token: localStorage.getItem("authToken") },
    });

    socket.on("transactionUpdated", (updatedTransaction) => {
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === updatedTransaction.id ? updatedTransaction : tx,
        ),
      );
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, recipientFilter, search, startDate, endDate]);

  const applyFilters = (tx) => {
    const txStatus = String(tx.status || "").toLowerCase();

    const matchesStatus =
      statusFilter === "all" || txStatus === String(statusFilter).toLowerCase();

    const rName = String(tx.recipientName || "").toLowerCase();
    const matchesRecipient =
      !recipientFilter.trim() ||
      rName.includes(recipientFilter.trim().toLowerCase());

    const phone = String(tx.recipientPhone || "");
    const ref = String(tx.paymentRef || tx.transactionNumber || tx.id || "");
    const s = search.trim();
    const matchesSearch =
      !s || phone.includes(s) || ref.toLowerCase().includes(s.toLowerCase());

    const created = tx.createdAt ? new Date(tx.createdAt) : null;
    const matchesDate =
      (!startDate || (created && created >= new Date(startDate))) &&
      (!endDate || (created && created <= new Date(endDate)));

    return matchesStatus && matchesRecipient && matchesSearch && matchesDate;
  };

  const filteredSorted = useMemo(() => {
    const filtered = transactions.filter(applyFilters);
    return filtered.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [transactions, statusFilter, recipientFilter, search, startDate, endDate]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSorted.length / ITEMS_PER_PAGE),
  );

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredSorted.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSorted, currentPage]);

  const statusBadge = (statusRaw) => {
    const status = String(statusRaw || "").toLowerCase();
    const base =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border";

    if (status === "pending")
      return `${base} border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300`;
    if (status === "completed")
      return `${base} border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300`;
    if (status === "cancelled" || status === "failed")
      return `${base} border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300`;

    return `${base} border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300`;
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm");
    } catch {
      return "-";
    }
  };

  const updateTxInState = (updated) => {
    setTransactions((prev) =>
      prev.map((t) => (t.id === updated.id ? updated : t)),
    );
  };

  const handleRepeatTx = (tx) => {
    navigate("/transaction", {
      state: {
        repeatTx: {
          countryTo: tx.destinationCountry || "",
          method: tx.method || "",
          currency: tx.currency || "",
          recipientCurrency: tx.recipientCurrency || "",
          beneficiary: {
            firstName: tx.recipientFirstName || "",
            lastName: tx.recipientLastName || "",
            fullName: tx.recipientName || "",
            phone: tx.recipientPhone || "",
            email: tx.recipientEmail || "",
          },
        },
      },
    });
  };

  const handleSaveNote = async (txId, note) => {
    try {
      const res = await axios.patch(`/api/transactions/${txId}/note`, { note });
      if (res.data?.transaction) updateTxInState(res.data.transaction);
    } catch (e) {
      console.error("Update note failed:", e);
      alert("Impossible de modifier la note.");
    }
  };

  const handleCancelTx = async (txId) => {
    try {
      const res = await axios.post(`/api/transactions/${txId}/cancel`);
      if (res.data?.transaction) updateTxInState(res.data.transaction);
    } catch (e) {
      console.error("Cancel failed:", e);
      alert("Annulation impossible (seulement 'pending').");
    }
  };

  const handleDeleteTx = async (txId) => {
    try {
      await axios.delete(`/api/transactions/${txId}`);
      setTransactions((prev) => prev.filter((t) => t.id !== txId));
    } catch (e) {
      console.error("Delete(hide) failed:", e);
      alert("Impossible de supprimer cette transaction.");
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Historique des transactions", 14, 16);

    const tableData = filteredSorted.map((t) => [
      t.paymentRef || t.transactionNumber || t.id,
      `${t.amount ?? "-"} ${t.currency ?? ""}`,
      `${t.convertedAmount ?? "-"} ${t.recipientCurrency ?? ""}`,
      `${getFee(t)} ${t.currency ?? ""}`,
      `${t.recipientName || "-"} (${t.recipientPhone || "-"})`,
      t.method || "-",
      t.destinationCountry || "-",
      t.status || "-",
      formatDate(t.createdAt),
    ]);

    autoTable(doc, {
      startY: 22,
      head: [
        [
          "Référence",
          "Montant envoyé",
          "Montant reçu",
          "Frais",
          "Bénéficiaire",
          "Méthode",
          "Pays",
          "Statut",
          "Date",
        ],
      ],
      body: tableData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [15, 23, 42] },
    });

    doc.save("transactions.pdf");
  };

  const handleDownloadReceipt = (tx) => {
    const doc = new jsPDF();

    try {
      doc.addImage(logo, "PNG", 14, 10, 22, 22);
    } catch {
      try {
        doc.addImage(logo, "JPEG", 14, 10, 22, 22);
      } catch {}
    }

    doc.setFontSize(16);
    doc.text("Reçu de transaction", 105, 18, { align: "center" });

    doc.setFontSize(11);

    const ref = tx.paymentRef || tx.transactionNumber || tx.id;

    const lines = [
      ["Référence", ref],
      ["Montant envoyé", `${tx.amount ?? "-"} ${tx.currency ?? ""}`],
      [
        "Montant reçu",
        `${tx.convertedAmount ?? "-"} ${tx.recipientCurrency ?? ""}`,
      ],
      ["Frais", `${getFee(tx)} ${tx.currency ?? ""}`],
      [
        "Bénéficiaire",
        `${tx.recipientName || "-"} (${tx.recipientPhone || "-"})`,
      ],
      ["Méthode", tx.method || "-"],
      ["Pays de destination", tx.destinationCountry || "-"],
      ["Note", tx.note || "-"],
      ["Statut", tx.status || "-"],
      ["Date", formatDate(tx.createdAt)],
    ];

    let y = 40;
    lines.forEach(([k, v]) => {
      doc.setTextColor(30);
      doc.text(`${k} :`, 14, y);
      doc.setTextColor(60);
      doc.text(String(v), 58, y);
      y += 9;
    });

    doc.setDrawColor(200);
    doc.line(14, y + 2, 196, y + 2);

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(
      "Merci d'avoir utilisé NGC Financial / Direct Money.",
      105,
      y + 12,
      {
        align: "center",
      },
    );

    doc.save(`recu_transaction_${ref}.pdf`);
  };

  const handlePrintReceipt = (tx) => {
    const ref = tx.paymentRef || tx.transactionNumber || tx.id;

    const html = `
      <html>
        <head>
          <title>Reçu ${ref}</title>
          <meta charset="utf-8" />
          <style>
            *{box-sizing:border-box;font-family: Arial, sans-serif;}
            body{margin:0;padding:24px;color:#0f172a;}
            .card{max-width:720px;margin:0 auto;border:1px solid #e2e8f0;border-radius:16px;padding:20px;}
            .header{display:flex;align-items:center;gap:14px;margin-bottom:16px;}
            .logo{width:44px;height:44px;border-radius:12px;border:1px solid #e2e8f0;object-fit:cover;}
            h1{font-size:18px;margin:0;}
            .muted{color:#64748b;font-size:12px;margin-top:2px;}
            .grid{display:grid;grid-template-columns: 1fr 1fr;gap:12px;margin-top:14px;}
            .row{padding:10px;border:1px solid #e2e8f0;border-radius:12px;}
            .k{font-size:11px;color:#64748b;margin-bottom:4px;}
            .v{font-size:13px;font-weight:600;color:#0f172a;word-break:break-word;}
            .full{grid-column:1/-1;}
            .footer{margin-top:16px;padding-top:12px;border-top:1px dashed #e2e8f0;font-size:12px;color:#64748b;text-align:center;}
            @media print { body{padding:0} .card{border:none} }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="header">
              <img class="logo" src="${logo}" />
              <div>
                <h1>Reçu de transaction</h1>
                <div class="muted">Référence : ${ref}</div>
              </div>
            </div>

            <div class="grid">
              <div class="row">
                <div class="k">Montant envoyé</div>
                <div class="v">${tx.amount ?? "-"} ${tx.currency ?? ""}</div>
              </div>
              <div class="row">
                <div class="k">Montant reçu</div>
                <div class="v">${tx.convertedAmount ?? "-"} ${tx.recipientCurrency ?? ""}</div>
              </div>

              <div class="row">
                <div class="k">Frais</div>
                <div class="v">${getFee(tx)} ${tx.currency ?? ""}</div>
              </div>
              <div class="row">
                <div class="k">Statut</div>
                <div class="v">${tx.status ?? "-"}</div>
              </div>

              <div class="row full">
                <div class="k">Bénéficiaire</div>
                <div class="v">${tx.recipientName || "-"} (${tx.recipientPhone || "-"})</div>
              </div>

              <div class="row">
                <div class="k">Méthode</div>
                <div class="v">${tx.method || "-"}</div>
              </div>
              <div class="row">
                <div class="k">Pays de destination</div>
                <div class="v">${tx.destinationCountry || "-"}</div>
              </div>

              <div class="row full">
                <div class="k">Note</div>
                <div class="v">${tx.note || "-"}</div>
              </div>

              <div class="row full">
                <div class="k">Date</div>
                <div class="v">${formatDate(tx.createdAt)}</div>
              </div>
            </div>

            <div class="footer">
              Merci d'avoir utilisé NGC Financial / Direct Money.
            </div>
          </div>

          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `;

    const w = window.open("", "_blank", "width=900,height=650");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="flex">
        <Sidebar />

        <main className="flex-1 pb-28 md:pb-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  Historique des transactions
                </h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Retrouvez toutes vos transactions et imprimez vos reçus.
                </p>
              </div>

              <button
                onClick={handleExportPDF}
                className="hidden sm:inline-flex items-center gap-2 rounded-2xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:opacity-95 transition
                           dark:bg-white dark:text-slate-900"
              >
                <FileDown className="h-4 w-4" />
                Export PDF
              </button>
            </div>

            {/* Filters */}
            <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Statut
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                  >
                    <option value="all">Tous</option>
                    <option value="pending">En cours</option>
                    <option value="completed">Complété</option>
                    <option value="cancelled">Annulé</option>
                    <option value="failed">Échec</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                    Nom bénéficiaire
                  </label>
                  <input
                    value={recipientFilter}
                    onChange={(e) => setRecipientFilter(e.target.value)}
                    placeholder="Ex: Jean"
                    className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Téléphone / Référence
                  </label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ex: 654..."
                    className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>

                <div className="md:col-span-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                      Du
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                      Au
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 sm:hidden">
                <button
                  onClick={handleExportPDF}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:opacity-95 transition
                             dark:bg-white dark:text-slate-900"
                >
                  <FileDown className="h-4 w-4" />
                  Export PDF
                </button>
              </div>
            </section>

            {loading ? (
              <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-6">
                <p className="text-slate-500 dark:text-slate-400">
                  Chargement…
                </p>
              </div>
            ) : filteredSorted.length === 0 ? (
              <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-6">
                <p className="text-slate-500 dark:text-slate-400">
                  Aucune transaction trouvée avec ces filtres.
                </p>
              </div>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="grid grid-cols-1 gap-3 md:hidden">
                  {paginated.map((tx) => {
                    const ref = tx.paymentRef || tx.transactionNumber || tx.id;
                    return (
                      <div
                        key={tx.id}
                        className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={statusBadge(tx.status)}>
                                {tx.status || "-"}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {formatDate(tx.createdAt)}
                              </span>
                            </div>

                            <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">
                              {tx.amount} {tx.currency}{" "}
                              <span className="text-slate-400">→</span>{" "}
                              {tx.convertedAmount} {tx.recipientCurrency}
                            </div>

                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                              <span className="font-medium">
                                {tx.recipientName || "-"}
                              </span>{" "}
                              <span className="text-slate-400">
                                ({tx.recipientPhone || "-"})
                              </span>
                            </div>

                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <div>
                                <span className="font-medium">Frais:</span>{" "}
                                {getFee(tx)} {tx.currency}
                              </div>
                              <div className="text-right">
                                <span className="font-medium">Pays:</span>{" "}
                                {tx.destinationCountry || "-"}
                              </div>
                              <div>
                                <span className="font-medium">Méthode:</span>{" "}
                                {tx.method || "-"}
                              </div>
                              <div className="text-right truncate">
                                <span className="font-medium">Ref:</span>{" "}
                                <span className="break-all">{ref}</span>
                              </div>
                            </div>
                          </div>

                          <ActionsMenu
                            onPrint={() => handlePrintReceipt(tx)}
                            onPdf={() => handleDownloadReceipt(tx)}
                            onNote={() => {
                              setActiveTx(tx);
                              setNoteModalOpen(true);
                            }}
                            onCancel={() => {
                              setActiveTx(tx);
                              setConfirmCancelOpen(true);
                            }}
                            onDelete={() => {
                              setActiveTx(tx);
                              setConfirmDeleteOpen(true);
                            }}
                            onRepeat={() => handleRepeatTx(tx)}
                            txStatus={tx.status}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-white/5">
                        <tr className="text-left text-slate-600 dark:text-slate-300">
                          <th className="px-4 py-3">Référence</th>
                          <th className="px-4 py-3">Montant envoyé</th>
                          <th className="px-4 py-3">Montant reçu</th>
                          <th className="px-4 py-3">Frais</th>
                          <th className="px-4 py-3">Bénéficiaire</th>
                          <th className="px-4 py-3">Méthode</th>
                          <th className="px-4 py-3">Pays</th>
                          <th className="px-4 py-3">Statut</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-black/5 dark:divide-white/10">
                        {paginated.map((tx) => {
                          const ref =
                            tx.paymentRef || tx.transactionNumber || tx.id;
                          return (
                            <tr
                              key={tx.id}
                              className="hover:bg-black/5 dark:hover:bg-white/5 transition"
                            >
                              <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                                {ref}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {tx.amount} {tx.currency}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {tx.convertedAmount} {tx.recipientCurrency}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {getFee(tx)} {tx.currency}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {tx.recipientName || "-"}
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                  {tx.recipientPhone || "-"}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {tx.method || "-"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {tx.destinationCountry || "-"}
                              </td>
                              <td className="px-4 py-3">
                                <span className={statusBadge(tx.status)}>
                                  {tx.status || "-"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                                {formatDate(tx.createdAt)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <ActionsMenu
                                  alignRight
                                  onPrint={() => handlePrintReceipt(tx)}
                                  onPdf={() => handleDownloadReceipt(tx)}
                                  onNote={() => {
                                    setActiveTx(tx);
                                    setNoteModalOpen(true);
                                  }}
                                  onCancel={() => {
                                    setActiveTx(tx);
                                    setConfirmCancelOpen(true);
                                  }}
                                  onDelete={() => {
                                    setActiveTx(tx);
                                    setConfirmDeleteOpen(true);
                                  }}
                                  onRepeat={() => handleRepeatTx(tx)}
                                  txStatus={tx.status}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {filteredSorted.length} transaction(s)
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="rounded-2xl border border-black/10 dark:border-white/10 px-3 py-2 text-sm
                                 bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                                 disabled:opacity-50"
                    >
                      Précédent
                    </button>

                    <span className="rounded-2xl border border-black/10 dark:border-white/10 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-white/60 dark:bg-white/5">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="rounded-2xl border border-black/10 dark:border-white/10 px-3 py-2 text-sm
                                 bg-white dark:bg-slate-900 text-slate-900 dark:text-white
                                 disabled:opacity-50"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Bottom nav (mobile only) */}
          <div className="md:hidden fixed bottom-0 left-0 w-full z-50">
            <BottomNav />
          </div>

          <NoteModal
            open={noteModalOpen}
            initialValue={activeTx?.note || ""}
            onClose={() => setNoteModalOpen(false)}
            onSave={async (val) => {
              if (!activeTx?.id) return;
              await handleSaveNote(activeTx.id, val);
              setNoteModalOpen(false);
            }}
          />

          <ConfirmDialog
            open={confirmCancelOpen}
            title="Annuler la transaction ?"
            desc="Vous pouvez annuler uniquement si la transaction est encore en attente (pending)."
            confirmText="Annuler"
            onClose={() => setConfirmCancelOpen(false)}
            onConfirm={async () => {
              if (!activeTx?.id) return;
              await handleCancelTx(activeTx.id);
              setConfirmCancelOpen(false);
            }}
          />

          <ConfirmDialog
            open={confirmDeleteOpen}
            title="Supprimer de l’historique ?"
            desc="Cette action retirera la transaction de votre historique."
            confirmText="Supprimer"
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={async () => {
              if (!activeTx?.id) return;
              await handleDeleteTx(activeTx.id);
              setConfirmDeleteOpen(false);
            }}
          />
        </main>
      </div>
    </div>
  );
}

/* ============================================================
   ACTIONS MENU (DESKTOP DROPDOWN + MOBILE BOTTOM SHEET)
============================================================ */

function ActionsMenu({
  onPrint,
  onPdf,
  onNote,
  onCancel,
  onDelete,
  onRepeat,
  alignRight,
  txStatus,
}) {
  const status = String(txStatus || "").toLowerCase();
  const canCancel = status === "pending";

  const [isMobile, setIsMobile] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Mobile: bottom sheet
  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex items-center justify-center rounded-xl border border-black/10 dark:border-white/10
                   bg-white dark:bg-slate-950 p-2 hover:bg-black/5 dark:hover:bg-white/10 transition"
          title="Actions"
        >
          <MoreVertical className="h-4 w-4 text-slate-700 dark:text-slate-200" />
        </button>

        <MobileActionSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Actions transaction"
        >
          <ActionBtn
            icon={<Printer className="h-4 w-4" />}
            label="Imprimer le reçu"
            onClick={() => {
              setSheetOpen(false);
              onPrint?.();
            }}
          />

          <ActionBtn
            icon={<FileDown className="h-4 w-4" />}
            label="Télécharger PDF"
            onClick={() => {
              setSheetOpen(false);
              onPdf?.();
            }}
          />

          <div className="my-2 h-px bg-black/5 dark:bg-white/10" />

          <ActionBtn
            label="✍️ Ajouter / modifier note"
            onClick={() => {
              setSheetOpen(false);
              onNote?.();
            }}
          />

          <ActionBtn
            label="🔁 Réeffectuer"
            onClick={() => {
              setSheetOpen(false);
              onRepeat?.();
            }}
          />

          <ActionBtn
            label="⛔ Annuler"
            disabled={!canCancel}
            onClick={() => {
              setSheetOpen(false);
              onCancel?.();
            }}
          />

          <ActionBtn
            label="🗑️ Supprimer de l’historique"
            danger
            onClick={() => {
              setSheetOpen(false);
              onDelete?.();
            }}
          />
        </MobileActionSheet>
      </>
    );
  }

  // Desktop dropdown
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        type="button"
        className="inline-flex items-center justify-center rounded-xl border border-black/10 dark:border-white/10
                   bg-white dark:bg-slate-950 p-2 hover:bg-black/5 dark:hover:bg-white/10 transition"
        title="Actions"
      >
        <MoreVertical className="h-4 w-4 text-slate-700 dark:text-slate-200" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-in"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Menu.Items
          className={`absolute mt-2 w-56 origin-top-right rounded-2xl border border-black/10 dark:border-white/10
                      bg-white dark:bg-slate-900 shadow-lg focus:outline-none z-50
                      ${alignRight ? "right-0" : "left-0"}`}
        >
          <div className="p-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onPrint}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} text-slate-800 dark:text-slate-100`}
                >
                  <Printer className="h-4 w-4" />
                  Imprimer le reçu
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onPdf}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} text-slate-800 dark:text-slate-100`}
                >
                  <FileDown className="h-4 w-4" />
                  Télécharger PDF
                </button>
              )}
            </Menu.Item>

            <div className="my-1 h-px bg-black/5 dark:bg-white/10" />

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onNote}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} text-slate-800 dark:text-slate-100`}
                >
                  ✍️ Ajouter / modifier note
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onRepeat}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} text-slate-800 dark:text-slate-100`}
                >
                  🔁 Réeffectuer
                </button>
              )}
            </Menu.Item>

            <Menu.Item disabled={!canCancel}>
              {({ active, disabled }) => (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={disabled}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} 
                    ${disabled ? "opacity-50 cursor-not-allowed" : ""} text-slate-800 dark:text-slate-100`}
                >
                  ⛔ Annuler
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onDelete}
                  className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm
                    ${active ? "bg-black/5 dark:bg-white/10" : ""} text-rose-700 dark:text-rose-300`}
                >
                  🗑️ Supprimer de l’historique
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function ActionBtn({ icon, label, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full flex items-center gap-2 rounded-2xl px-4 py-3 text-sm text-left transition",
        "border border-black/5 dark:border-white/10",
        "bg-white dark:bg-slate-950 hover:bg-black/5 dark:hover:bg-white/10",
        disabled ? "opacity-50 cursor-not-allowed" : "",
        danger
          ? "text-rose-700 dark:text-rose-300"
          : "text-slate-900 dark:text-slate-100",
      ].join(" ")}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="font-medium">{label}</span>
    </button>
  );
}

function MobileActionSheet({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute inset-x-0 bottom-0">
        <div className="mx-auto max-w-lg">
          <div className="rounded-t-3xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl">
            <div className="p-4">
              <div className="mx-auto h-1.5 w-12 rounded-full bg-black/10 dark:bg-white/10" />
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                  {title || "Actions"}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl px-3 py-1.5 text-sm border border-black/10 dark:border-white/10
                             bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                >
                  Fermer
                </button>
              </div>

              <div className="mt-4 space-y-2 max-h-[60vh] overflow-y-auto pb-3">
                {children}
              </div>
            </div>
          </div>

          {/* espace bas pour éviter collision avec BottomNav */}
          <div className="h-20 md:hidden" />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MODALS (SCROLL-SAFE MOBILE)
============================================================ */

function NoteModal({ open, onClose, initialValue, onSave }) {
  const [val, setVal] = React.useState(initialValue || "");

  React.useEffect(() => {
    setVal(initialValue || "");
  }, [initialValue]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 shadow-xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">
          Ajouter / modifier une note
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Mémo personnel (max 500 caractères).
        </p>

        <textarea
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={5}
          className="mt-3 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-white outline-none"
          placeholder="Ex: Paiement loyer / famille / facture..."
          maxLength={500}
        />

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-2xl border border-black/10 dark:border-white/10 px-4 py-2 text-sm bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
          >
            Annuler
          </button>
          <button
            onClick={() => onSave(val)}
            className="rounded-2xl px-4 py-2 text-sm bg-slate-900 text-white dark:bg-white dark:text-slate-900"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  title,
  desc,
  confirmText = "Confirmer",
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 shadow-xl p-5 max-h-[80vh] overflow-y-auto">
        <div className="text-sm font-semibold text-slate-900 dark:text-white">
          {title}
        </div>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {desc}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-2xl border border-black/10 dark:border-white/10 px-4 py-2 text-sm bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
          >
            Retour
          </button>
          <button
            onClick={onConfirm}
            className="rounded-2xl px-4 py-2 text-sm bg-rose-600 text-white"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
